import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import * as REQUEST_REQUIREMENTS from '../../api/requestRequirements';
import { getSearchResults } from '../sessions/sessionSearch/sliceSearch';
import { InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

// — Design tokens —
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';

interface SearchBarProps {
  searchInputQuery: string;
  setSearchInputQuery: Dispatch<SetStateAction<string>>;
  setShowComponent: Dispatch<SetStateAction<boolean>>;
}

function SearchBar({ searchInputQuery, setSearchInputQuery, setShowComponent }: SearchBarProps) {
  const navigate = useNavigate();
  const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowComponent(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({
    ROUTE_PARAMS: {
      queryType: 'users',
      searchInputQuery: searchInputQuery,
    },
  });

  async function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      const currentPath = location.pathname;
      const targetPath = PRIVATE_ROUTES.SEARCH_BAR_NAVIGATION.split('?')[0];

      if (searchInputQuery) {
        if (currentPath === targetPath) {
          await fetchUsersData();
        }
        navigate(PRIVATE_ROUTES.SEARCH_BAR_NAVIGATION);
      }
    }
  }

  async function fetchUsersData() {
    if (authHeaders && searchInputQuery) {
      await dispatch(
        getSearchResults({
          headers: authHeaders,
          routeParams: {
            queryType: 'users',
            query: searchInputQuery,
            page: 1,
          },
        })
      );
    }
  }

  return (
    <div ref={containerRef} style={{ width: '100%', maxWidth: 400 }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search..."
        value={searchInputQuery}
        onChange={(e) => setSearchInputQuery(e.target.value)}
        onKeyDown={handleKeyPress}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: TEXT_SECONDARY, fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            color: TEXT_PRIMARY,
            bgcolor: BG_CARD,
            borderRadius: '20px',
            '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },

        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px #1c1c24 inset`,
            WebkitTextFillColor: '#f4f4f6',
            caretColor: '#f4f4f6',
        },
            '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
          },
          '& .MuiInputLabel-root': { color: TEXT_SECONDARY },
          '& .MuiInputLabel-root.Mui-focused': { color: '#f4f4f6', fontWeight: 700 },
          '& input::placeholder': { color: TEXT_SECONDARY, opacity: 1 },
        }}
      />
    </div>
  );
}

export default SearchBar;
